import { ApiClientError } from "./errors";

/** Falha de gateway ou rede. Senha errada e 4xx de negócio ficam de fora. */
export class HttpStatusError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "HttpStatusError";
    this.status = status;
  }
}

export function isGatewayStatus(status: number): boolean {
  return status === 502 || status === 503 || status === 504;
}

/**
 * Espera do 429. `null` = não tentar de novo (pediu mais que 8s).
 * Sem header, espera 1s.
 */
export function retryAfterWaitMs(header: string | null): number | null {
  const raw = (header || "").trim();
  if (!raw) return 1000;
  const seconds = Number(raw);
  if (!Number.isFinite(seconds) || seconds < 0) return 1000;
  if (seconds > 8) return null;
  return Math.round(seconds * 1000);
}

export function transientBackoffMs(attempt: number): number {
  const base = attempt <= 0 ? 400 : 1200;
  return base + Math.floor(Math.random() * 250);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/** Até 3 tentativas. 429 no máximo uma, respeitando Retry-After. */
export async function fetchWithTransientRetry(
  input: string,
  init: RequestInit = {}
): Promise<Response> {
  let rateUsed = false;
  let lastError: unknown;
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const res = await fetch(input, init);
      if (res.status === 429 && !rateUsed) {
        const wait = retryAfterWaitMs(res.headers.get("Retry-After"));
        if (wait == null) return res;
        rateUsed = true;
        await sleep(wait);
        continue;
      }
      if (isGatewayStatus(res.status) && attempt < 2) {
        await sleep(transientBackoffMs(attempt));
        continue;
      }
      return res;
    } catch (err) {
      lastError = err;
      const name = (err as { name?: string })?.name;
      if (name === "AbortError" || attempt >= 2) throw err;
      await sleep(transientBackoffMs(attempt));
    }
  }
  throw lastError instanceof Error ? lastError : new Error("network");
}

/** Mensagem do login quando a chamada falhou. Senha errada não passa por aqui. */
export function authFailureKey(error: unknown): string {
  const status =
    error instanceof HttpStatusError
      ? error.status
      : error instanceof ApiClientError
        ? error.status
        : -1;
  if (status === 429) return "auth.error.rate_limited";
  if (
    status === 0 ||
    status === 502 ||
    status === 503 ||
    status === 504 ||
    error instanceof TypeError ||
    (error instanceof Error && error.name === "AbortError")
  ) {
    return "auth.error.server_unreachable";
  }
  return "auth.error.login_failed";
}
